import React, { useCallback, useEffect } from 'react';
import KeepAlive from 'react-activation';
import { useDispatch, useSelector } from 'react-redux';
import { BrowserRouter as Router, Redirect, Route, Switch } from 'react-router-dom';
import { createSelector } from 'reselect';
import { updateIsSIMRunning, updateSIMProgress, updateSIMStatus } from './actions/asimstatus';
import {
  addWelcomeIdToDownload,
  removeDownload,
  updateDownloadProgress,
  updateDownloadSoftStatus,
} from './actions/download';
import { setDownloadVisible } from './actions/layout';
import { logging } from './actions/log';
import {
  getLocalSoft,
  getSilentInstallSoft,
  getUpgradeSofts,
  removeUninstall,
  setCommandLine,
  setOtherInfo,
  setQuietInstallPath,
  setWelcomeSoftID,
} from './actions/native';
import {
  addTempResidual,
  cancelUninstall,
  modifyResidualData,
  removeResidual,
  removeTempResidual,
  stopScanSoftwareResidual,
} from './actions/uninstall';
import { updateUserInfo } from './actions/user';
import {
  removeDownloadImg,
  updateDownloadImgProgress,
  updateDownloadImgStatus,
} from './actions/wallpaper';
import { getWelcomeData } from './actions/welcome';
import { setFeedbackTask } from './actions/userFeedback';
import { CONTEXT } from './config';
import {
  APKS_NT_STATUS,
  SIM_DOWNLOAD_FAILED,
  SIM_DOWNLOAD_FINISHED,
  SIM_DOWNLOAD_ING,
  SIM_INSTALL_FAILED,
  SIM_INSTALL_FINISHED,
  SIM_INSTALL_ING,
  SIM_PREPARE,
  SIM_PREPARE_DONE,
  SIM_PREPARE_FAILED,
  SIM_PREPARING,
  SIM_UPDATE_INSTALL_FAILED,
  SIM_UPDATE_INSTALL_FINISHED,
  // 模拟器升级
  SIM_UPDATE_INSTALL_ING,
  SIM_VM_DESKTOP,
  SIM_VM_DESKTOP_FAILED,
  // SIM_DOWNLOAD_CANCELED,
  SIM_VM_RUN,
} from './constants/asimstatus';
import {
  ALL_STATUS,
  DOWNLOAD_CANCELED,
  DOWNLOAD_FAILED,
  DOWNLOAD_FINISHED,
  DOWNLOAD_ING,
  INSTALL_FAILED,
  INSTALL_FINISHED,
} from './constants/softstatus';
import { cacheKeys } from './constants/storageKeys';
import {
  WP_DOWNLOAD_CANCELED,
  WP_DOWNLOAD_FAILED,
  WP_DOWNLOAD_FINISHED,
} from './constants/wallpaper';

import { logNative, getDefaultPage } from './utils/utils';
import Layout from './layouts/Layout';
import { PCStoreJsBridge } from './nativeapi';
import ACategory from './pages/ACategory/ACategory';
import ADetail from './pages/ADetail/SoftDetail';
import ARecommend from './pages/ARecommend/ARecommend';
import Category from './pages/Category/Category';
import RankMore from './pages/Comm/RankMore';
import DataTest from './pages/DataTest/DataTest';
import ErrorPage from './pages/ErrorPage/ErrorPage';
import ErrorPageEdu from './pages/ErrorPage/ErrorPageEdu';
import Game from './pages/Game';
import RecoEducation from './pages/Education/EducationTabs/EducationTabs';
import GuessLikesMore from './pages/Recommend/GuessLikesMore';
import Recommend from './pages/Recommend/Recommend';
import RecoWork from './pages/RecoWork/RecoWork';
import SearchResult from './pages/SearchResult/SearchResult';
import Shop from './pages/Shop/Shop';
import Detail from './pages/Detail/SoftDetail';
import Special from './pages/Special/Special';
import SpecialList from './pages/Special/SpecialList';
import Topn from './pages/Topn/Topn';
import TopnMore from './pages/Topn/TopnMore';
import Uninstall from './pages/Uninstall/Uninstall';
import Update from './pages/Update/Update';
import WallPaperAuthorDetail from './pages/Wallpaper/WallPaperAuthorDetail';
import WallPaperAuthorList from './pages/Wallpaper/WallPaperAuthorList';
import WallpaperCategory from './pages/Wallpaper/WallpaperCategory';
import WallpaperDetail from './pages/Wallpaper/WallpaperDetail';
import WallpaperMine from './pages/Wallpaper/WallpaperMine';
import WallPaperRank from './pages/Wallpaper/WallPaperRank';
import WallpaperRecommend from './pages/Wallpaper/WallpaperRecommend';

import Collection from './pages/Collection/Collection';
import EduDetail from './pages/Education/EduDetail/EduDetail';
import OtherPage from './pages/OtherPage/OtherPage';
import OtherTest from './pages/OtherPage/OtherTest';

import ForumDetail from './pages/Forum/ForumDetail/ForumDetail';
import ForumPublish from './pages/Forum/ForumPublish/ForumPublish';
import ForumList from './pages/Forum/ForumList/ForumList';
import ForumListCollect from './pages/Forum/ForumListCollect/ForumListCollect';
import ForumListPublish from './pages/Forum/ForumListPublish/ForumListPublish';

const selectNeedShow = createSelector(
  state => state.WelcomApp,
  WelcomApp => WelcomApp.needShow,
);

const selectWelcomeIDs = createSelector(
  state => state.native,
  native => native.ids,
);

const selectLocalSofts = createSelector(
  state => state.native,
  native => native.installedSoft,
);

const selectSIMProgress = createSelector(
  state => state.asimstatus,
  asimstatus => asimstatus.progress,
);
const selectClearStatus = createSelector(
  state => state.uninstall,
  uninstall => uninstall.clearStatus,
);

function App() {
  const dispatch = useDispatch();
  const needShow = useSelector(selectNeedShow);
  const welcomeIDs = useSelector(selectWelcomeIDs);
  const localSofts = useSelector(selectLocalSofts);
  const progress = useSelector(selectSIMProgress); // 模拟器进度
  const clearStatus = useSelector(selectClearStatus); // 卸载残留清理进程状态

  useEffect(() => {
    if (localSofts.length === 0) {
      dispatch(getLocalSoft());
    }
  }, [dispatch, localSofts.length]);

  const updateProgress = useCallback(
    newProg => {
      if (newProg === 0 || newProg === 100) {
        dispatch(updateSIMProgress({ progress: newProg }));
      } else if (progress < newProg) {
        const progArr = [newProg, newProg + 1, newProg + 2, newProg + 3, newProg + 4];
        dispatch(updateSIMProgress({ progress: progArr[Math.floor(Math.random() * 5)] }));
      }
    },
    [dispatch, progress],
  );

  /**
   * 对于安卓应用通知状态的处理
   */
  useEffect(() => {
    PCStoreJsBridge.subscribeMessage(message => {
      if (APKS_NT_STATUS.indexOf(message.getMsgcategory()) > -1) {
        let retJson = {};
        const ret = PCStoreJsBridge.arrayToUTF8String(message.getMessageproto());
        try {
          retJson = JSON.parse(ret);
        } catch (error) {
          logNative(`通知 SIM JSON 解析错误:${error}`, error);
        }
        let statusObj = {};
        switch (message.getMsgcategory()) {
          case 'APKS_NT_SIM_PREPARE':
            window.SIMTime.SIMRunStartTime = new Date().getTime();
            logNative('通知 APKS_NT_SIM_PREPARE 模拟器开始准备', retJson);
            dispatch(
              logging({
                category: 'Simulate',
                action: 'Start_Simulate',
                label: '',
                param2: {
                  launchid: window.SIMTime.SIMRunStartTime,
                  time: window.SIMTime.SIMRunStartTime - window.SIMTime.SIMRunStartTime,
                },
              }),
            );

            dispatch(updateSIMStatus({ status: SIM_PREPARE }));
            updateProgress(0);
            break;
          case 'APKS_NT_SIM_DL_FETCH_INFO':
            logNative(
              '通知 APKS_NT_SIM_DL_FETCH_INFO 开始下载之前，即预下载准备阶段，比如获取下载URL信息',
              retJson,
            );
            break;
          case 'APKS_NT_SIM_DL_FETCH_INFO_DONE':
            logNative('通知 APKS_NT_SIM_DL_FETCH_INFO_DONE 开始下载之前，预下载完成', retJson);
            break;
          case 'APKS_NT_SIM_DL_START':
            window.SIMTime.SIMDLStartTime = new Date().getTime();
            logNative(
              `通知 APKS_NT_SIM_DL_START 模拟器下载开始: ${window.SIMTime.SIMDLStartTime -
                window.SIMTime.SIMRunStartTime}`,
              retJson,
            );
            dispatch(
              logging({
                category: 'Simulate',
                action: 'D_Sd',
                label: '',
                param2: {
                  launchid: window.SIMTime.SIMRunStartTime,
                  time: window.SIMTime.SIMDLStartTime - window.SIMTime.SIMRunStartTime,
                  type: 'd',
                },
              }),
            );

            // 特殊处理
            dispatch(updateSIMStatus({ status: SIM_PREPARING }));
            updateProgress(1);
            break;
          case 'APKS_NT_SIM_DL_PROGRESS':
            logNative(
              `通知 APKS_NT_SIM_DL_PROGRESS 模拟器下载进度: ${parseFloat(retJson.dlsz) /
                parseFloat(retJson.sz)}`,
              retJson,
              true,
              true,
            );
            dispatch(updateSIMStatus({ status: SIM_DOWNLOAD_ING }));
            dispatch(
              updateSIMProgress({
                progress:
                  parseInt((parseFloat(retJson.dlsz) / parseFloat(retJson.sz)) * 70, 10) + 5,
              }),
            );
            break;
          case 'APKS_NT_SIM_DL_DONE':
            window.SIMTime.SIMDownEndTime = new Date().getTime();
            logNative(
              `通知 APKS_NT_SIM_INSTALL  模拟器下载结束: ${window.SIMTime.SIMDownEndTime -
                window.SIMTime.SIMRunStartTime}`,
              retJson,
            );
            dispatch(
              logging({
                category: 'Simulate',
                action: 'D_Ed',
                label: '',
                param2: {
                  launchid: window.SIMTime.SIMRunStartTime,
                  msg: retJson.msg,
                  code: retJson.code,
                  status: retJson.succ,
                  time: window.SIMTime.SIMDownEndTime - window.SIMTime.SIMRunStartTime,
                  type: 'd',
                },
              }),
            );

            dispatch(
              updateSIMStatus({
                status: !retJson.succ ? SIM_DOWNLOAD_FAILED : SIM_DOWNLOAD_FINISHED,
              }),
            );
            updateProgress(76);
            break;
          case 'APKS_NT_SIM_INSTALL':
            window.SIMTime.SIMInstallTime = new Date().getTime();
            logNative(
              `通知 APKS_NT_SIM_INSTALL  模拟器开始安装: ${window.SIMTime.SIMInstallTime -
                window.SIMTime.SIMRunStartTime}`,
              retJson,
            );
            dispatch(
              logging({
                category: 'Simulate',
                action: 'I_Si',
                label: '',
                param2: {
                  launchid: window.SIMTime.SIMRunStartTime,
                  time: window.SIMTime.SIMInstallTime - window.SIMTime.SIMRunStartTime,
                  type: 'd',
                },
              }),
            );

            dispatch(updateSIMStatus({ status: SIM_INSTALL_ING }));
            updateProgress(81);
            break;
          case 'APKS_NT_SIM_INSTALL_DONE':
            window.SIMTime.SIMInstEndTime = new Date().getTime();
            logNative(
              `通知 APKS_NT_SIM_INSTALL_DONE  模拟器安装完成: ${window.SIMTime.SIMInstEndTime -
                window.SIMTime.SIMRunStartTime}`,
              retJson,
            );
            dispatch(
              logging({
                category: 'Simulate',
                action: 'I_Ei',
                label: '',
                param2: {
                  launchid: window.SIMTime.SIMRunStartTime,
                  msg: retJson.msg,
                  code: retJson.code,
                  status: retJson.succ,
                  time: window.SIMTime.SIMInstEndTime - window.SIMTime.SIMRunStartTime,
                  type: 'd',
                },
              }),
            );

            dispatch(
              updateSIMStatus({
                status: retJson.succ !== 'true' ? SIM_INSTALL_FAILED : SIM_INSTALL_FINISHED,
              }),
            );
            updateProgress(86);
            break;
          case 'APKS_NT_SIM_UPGRADE':
            window.SIMTime.SIMUpdateInstStartTime = new Date().getTime();
            logNative(
              `通知 APKS_NT_SIM_UPGRADE  模拟器升级安装开始: ${window.SIMTime
                .SIMUpdateInstStartTime - window.SIMTime.SIMRunStartTime}`,
              retJson,
            );
            dispatch(
              logging({
                category: 'Simulate',
                action: 'I_Si',
                label: '',
                param2: {
                  launchid: window.SIMTime.SIMRunStartTime,
                  time: window.SIMTime.SIMUpdateInstStartTime - window.SIMTime.SIMRunStartTime,
                  type: 'u',
                },
              }),
            );
            dispatch(updateSIMStatus({ status: SIM_UPDATE_INSTALL_ING }));
            break;
          case 'APKS_NT_SIM_UPGRADE_DONE':
            window.SIMTime.SIMUpdateInstEndTime = new Date().getTime();
            logNative(
              `通知 APKS_NT_SIM_UPGRADE_DONE  模拟器升级安装完成: ${window.SIMTime
                .SIMUpdateInstEndTime - window.SIMTime.SIMUpdateInstStartTime}`,
              retJson,
            );
            dispatch(
              logging({
                category: 'Simulate',
                action: 'I_Ei',
                label: '',
                param2: {
                  launchid: window.SIMTime.SIMRunStartTime,
                  msg: retJson.msg,
                  code: retJson.code,
                  status: retJson.succ,
                  time: window.SIMTime.SIMUpdateInstEndTime - window.SIMTime.SIMUpdateInstStartTime,
                  type: 'u',
                },
              }),
            );
            dispatch(
              updateSIMStatus({
                status:
                  retJson.succ !== 'true' ? SIM_UPDATE_INSTALL_FAILED : SIM_UPDATE_INSTALL_FINISHED,
              }),
            );
            break;
          case 'APKS_NT_VM_RUN':
            window.SIMTime.SIMVMRunTime = new Date().getTime();
            logNative(
              `通知 APKS_NT_VM_RUN  模拟器VM运行开始: ${window.SIMTime.SIMVMRunTime -
                window.SIMTime.SIMRunStartTime}`,
              retJson,
              true,
            );
            dispatch(
              logging({
                category: 'Simulate',
                action: 'VM_Simulate',
                label: '',
                param2: {
                  launchid: window.SIMTime.SIMRunStartTime,
                  time: window.SIMTime.SIMVMRunTime - window.SIMTime.SIMRunStartTime,
                },
              }),
            );

            dispatch(updateSIMStatus({ status: SIM_VM_RUN }));
            updateProgress(91);
            break;
          case 'APKS_NT_VM_DESKTOP':
            window.SIMTime.SIMVMDesktopRunTime = new Date().getTime();
            logNative(
              `通知 APKS_NT_VM_DESKTOP  模拟器VM桌面启动结果: ${window.SIMTime.SIMVMDesktopRunTime -
                window.SIMTime.SIMRunStartTime}`,
              retJson,
            );
            dispatch(
              logging({
                category: 'Simulate',
                action: 'VM_DESKTOP_Simulate',
                label: '',
                param2: {
                  launchid: window.SIMTime.SIMRunStartTime,
                  msg: retJson.msg,
                  code: retJson.code,
                  status: retJson.succ,
                  time: window.SIMTime.SIMVMDesktopRunTime - window.SIMTime.SIMRunStartTime,
                },
              }),
            );

            dispatch(
              updateSIMStatus({
                status: retJson.succ !== 'true' ? SIM_VM_DESKTOP_FAILED : SIM_VM_DESKTOP,
              }),
            );
            // 模拟器启动失败重新获取一个本地列表  从而更改按钮（安装，更新，卸载）状态
            if (retJson.succ !== 'true') {
              dispatch(getLocalSoft());
            }
            updateProgress(96);
            break;
          case 'APKS_NT_SIM_PREPARE_DONE':
            window.SIMTime.SIMRunEndTime = +new Date();
            logNative(
              `通知 APKS_NT_SIM_PREPARE_DONE  模拟器准备结束: ${window.SIMTime.SIMRunEndTime -
                window.SIMTime.SIMRunStartTime}`,
              retJson,
            );
            dispatch(
              logging({
                category: 'Simulate',
                action: 'End_Simulate',
                label: '',
                param2: {
                  launchid: window.SIMTime.SIMRunStartTime,
                  msg: retJson.msg,
                  code: retJson.code,
                  status: retJson.succ,
                  time: window.SIMTime.SIMRunEndTime - window.SIMTime.SIMRunStartTime,
                },
              }),
            );

            dispatch(
              updateSIMStatus({
                status: retJson.succ !== 'true' ? SIM_PREPARE_FAILED : SIM_PREPARE_DONE,
              }),
            );
            // 模拟器启动失败重新获取一个本地列表  从而更改按钮（安装，更新，卸载）状态
            if (retJson.succ !== 'true') {
              dispatch(getLocalSoft());
            }
            if (progress <= 100) {
              dispatch(updateSIMProgress({ progress: 100 }));
            }

            // 更新是否启动模拟器
            dispatch(updateIsSIMRunning({ isSIMRunning: retJson.succ === 'true' }));
            break;
          case 'APKS_NT_DL_START':
            window.SIMTime.SIMApkDownStartTime = +new Date();
            logNative(
              `通知 APKS_NT_DL_START  apk开始下载: ${window.SIMTime.SIMApkDownStartTime -
                window.SIMTime.SIMApkDownStartTime}`,
              retJson,
            );

            statusObj = {
              SoftID: retJson.softid,
              packageName: retJson.pkg,
              versionCode: retJson.vc,
              versionName: retJson.vn,
              status: DOWNLOAD_ING,
              taskid: retJson.taskid,
            };
            dispatch(updateDownloadSoftStatus(statusObj));
            break;
          case 'APKS_NT_DL_PROGRESS':
            logNative(
              `通知 APKS_NT_DL_PROGRESS  apk下载进度: ${parseFloat(retJson.dlsz) /
                parseFloat(retJson.sz)}}`,
              retJson,
              true,
              true,
            );

            statusObj = {
              SoftID: retJson.softid,
              packageName: retJson.pkg,
              versionCode: retJson.vc,
              versionName: retJson.vn,
              AverageDownloadSpeed: retJson.avspeed,
              DownloadSpeed: retJson.dlspeed,
              progress: parseInt((parseFloat(retJson.dlsz) / parseFloat(retJson.sz)) * 100, 10),
              dlsz: retJson.dlsz,
            };
            dispatch(updateDownloadProgress(statusObj));
            break;
          case 'APKS_NT_DL_DONE':
            window.SIMTime.SIMApkDownEndTime = +new Date();
            logNative(
              `通知 APKS_NT_DL_DONE  app下载结束: ${window.SIMTime.SIMApkDownEndTime -
                window.SIMTime.SIMApkDownStartTime}}`,
              retJson,
            );

            statusObj = {
              SoftID: retJson.softid,
              packageName: retJson.pkg,
              versionCode: retJson.vc,
              versionName: retJson.vn,
              taskid: retJson.taskid,
              status: retJson.succ !== 'true' ? DOWNLOAD_FAILED : DOWNLOAD_FINISHED,
            };
            // 安卓软件apk 下载失败   就马上调用客户端接口，删除后端下载安装任务，前端界面的下载任务视图仍然保留
            if (retJson.succ !== 'true') {
              PCStoreJsBridge.cancelAndroidDownload({
                ...statusObj,
                softID: retJson.softid,
                version: retJson.vn,
              });
            }
            if (!retJson.succ) {
              dispatch(removeDownload(statusObj.SoftID));
            }
            dispatch(updateDownloadSoftStatus(statusObj));
            break;
          case 'APKS_NT_INSTALL_START':
            logNative(`通知 APKS_NT_INSTALL_START  apk开始安装`, retJson);

            statusObj = {
              SoftID: retJson.softid,
              packageName: retJson.pkg,
              versionCode: retJson.vc,
              versionName: retJson.vn,
              taskid: retJson.taskid,
              status: DOWNLOAD_FINISHED,
            };
            dispatch(updateDownloadSoftStatus(statusObj));
            break;
          case 'APKS_NT_INSTALL_DONE':
            window.SIMTime.SIMApkInstEndTime = +new Date();
            logNative(
              `通知 APKS_NT_INSTALL_DONE  apk安装结束: ${window.SIMTime.SIMApkInstEndTime -
                window.SIMTime.SIMApkDownEndTime}}`,
              retJson,
            );

            statusObj = {
              SoftID: retJson.softid,
              packageName: retJson.pkg,
              versionCode: retJson.vc,
              versionName: retJson.vn,
              taskid: retJson.taskid,
              status: retJson.succ === 'true' ? INSTALL_FINISHED : INSTALL_FAILED,
            };
            dispatch(updateDownloadSoftStatus(statusObj));
            // 安卓软件apk 下载失败   就马上调用客户端接口，删除后端下载安装任务，前端界面的下载任务视图仍然保留
            if (retJson.succ !== 'true') {
              PCStoreJsBridge.cancelAndroidDownload({
                ...statusObj,
                softID: retJson.softid,
                version: retJson.vn,
              });
            } else {
              dispatch(removeDownload(statusObj.SoftID));
            }
            dispatch(getLocalSoft());
            break;
          case 'APKS_NT_INSTALL_CANCEL':
            logNative(`通知 APKS_NT_INSTALL_CANCEL`, retJson);
            break;
          case 'APKS_NT_UNINSTALL_START':
            logNative(`通知 APKS_NT_UNINSTALL_START`, retJson);
            break;
          case 'APKS_NT_UNINSTALL_DONE':
            logNative(`通知 APKS_NT_UNINSTALL_DONE`, retJson);
            break;
          case 'APKS_NT_RUN_START':
            window.SIMTime.SIMApkRunStartTime = +new Date();
            logNative(`通知 APKS_NT_RUN_START apk软件启动开始`, retJson);
            // apk开始运行
            dispatch(
              logging({
                category: 'Simulate',
                action: 'Start_App',
                label: '',
                param2: {
                  packageName: retJson.pkg,
                  versionCode: retJson.vc,
                  versionName: retJson.vn,
                },
              }),
            );
            break;
          case 'APKS_NT_RUN_DONE':
            window.SIMTime.SIMApkRunEndTime = +new Date();
            logNative(
              `通知 APKS_NT_RUN_DONE apk软件启动结束: ${window.SIMTime.SIMApkRunEndTime -
                window.SIMTime.SIMApkRunStartTime}`,
              retJson,
            );
            // apk结束运行
            dispatch(
              logging({
                category: 'Simulate',
                action: 'End_App',
                label: '',
                param2: {
                  msg: retJson.msg,
                  code: retJson.code,
                  status: retJson.succ,
                  time: window.SIMTime.SIMApkRunEndTime - window.SIMTime.SIMApkRunStartTime,
                  packageName: retJson.pkg,
                  versionCode: retJson.vc,
                  versionName: retJson.vn,
                },
              }),
            );
            break;
          case 'APKS_PC_NT_UPGRADE_LIST':
            logNative(
              `通知 APKS_PC_NT_UPGRADE_LIST PC/安卓List 获取更新耗时: ${new Date().getTime() -
                Number(window.getUpdateStartTime)}ms, 可更新: ${retJson.data &&
                retJson.data.length}个`,
              retJson,
              true,
            );
            dispatch(getUpgradeSofts(retJson.data || []));
            break;
          default:
            logNative(`通知 default 客户端未处理通知 ${message}`);
            break;
        }
      }
    });
  }, []);

  /**
   * 对于PC应用，客户端通知状态的处理
   */
  useEffect(() => {
    PCStoreJsBridge.subscribeMessage(message => {
      // 用户登录  退出登录通知
      if (message.getMsgcategory() === 'PCM_MESSAGE_NOTIFY_LOGIN') {
        const ret = PCStoreJsBridge.arrayToUTF8String(message.getMessageproto());
        try {
          const retJson = JSON.parse(ret);
          logNative(`通知 PCM_MESSAGE_NOTIFY_LOGIN 登录信息变更刷新`, retJson);
          dispatch(
            logging({
              category: 'R',
              action: 'Login_Update',
              label: '',
              param2: { userInfo: retJson },
            }),
          );
          dispatch(updateUserInfo(retJson));
        } catch (error) {
          logNative(`通知 PCM_MESSAGE_NOTIFY_LOGIN JSON 解析错误:${error}`, error);
        }
      }

      // 启动session信息
      if (message.getMsgcategory() === 'ON_START_INFO') {
        const ret = PCStoreJsBridge.arrayToUTF8String(message.getMessageproto());
        try {
          const retJson = JSON.parse(ret);
          logNative(`通知 ON_START_INFO 商店启动信息：`, {
            ...retJson,
            isDStore: retJson.leas_runtime === 'independent',
          });
          localStorage.setItem(
            cacheKeys.START_INFO,
            JSON.stringify({
              ...retJson,
              isDStore: retJson.leas_runtime === 'independent',
            }),
          );
        } catch (error) {
          logNative(`通知 ON_START_INFO JSON 解析错误:${error}`, error);
        }
      }

      // 启动命令行
      if (message.getMsgcategory() === 'ON_START_COMMOANDLINE') {
        const ret = PCStoreJsBridge.arrayToUTF8String(message.getMessageproto());
        const cmd = ret.trim();
        if (cmd) {
          logNative(`通知 ON_START_COMMOANDLINE setCommandLine ${cmd}`);
          dispatch(setCommandLine(cmd));
        }
      }

      // 商店打开着  调用命令跳入某个页面（目前为命令跳入详情页面，并开始下载应用）
      if (message.getMsgcategory() === 'ON_WINDOW_WM_COPYDATA') {
        const ret = JSON.parse(PCStoreJsBridge.arrayToUTF8String(message.getMessageproto()));
        if (ret && ret.data.trim()) {
          logNative(`通知 ON_WINDOW_WM_COPYDATA setCommandLine ${ret.data.trim()}`);
          dispatch(setCommandLine(ret.data.trim()));
        }
      }

      if (message.getMsgcategory() === 'APPSTORE_SYSCOMMAND') {
        // const ret = PCStoreJsBridge.arrayToUTF8String(message.getMessageproto());
      }

      if (message.getMsgcategory() === 'APPSTORE_WELCOME_SOFTSID') {
        const ret = PCStoreJsBridge.arrayToUTF8String(message.getMessageproto());
        dispatch(setWelcomeSoftID(ret));
      }

      // 下载进度变化通知
      if (message.getMsgcategory() === 'APPSTORE_DOWNLOADPROGRESS') {
        const ret = PCStoreJsBridge.arrayToUTF8String(message.getMessageproto());
        try {
          const retJson = JSON.parse(ret);
          logNative(`通知 APPSTORE_DOWNLOADPROGRESS  PC软件下载进度`, retJson, true, true);
          dispatch(updateDownloadProgress(retJson));
        } catch (error) {
          logNative(`通知 APPSTORE_DOWNLOADPROGRESS JSON 解析错误:${error}`, error);
        }
      }

      // 下载状态变化通知
      if (message.getMsgcategory() === 'APPSTORE_DOWNLOADDING_STATUS') {
        const ret = PCStoreJsBridge.arrayToUTF8String(message.getMessageproto());
        try {
          const retJson = JSON.parse(ret);
          // 新逻辑只处理我定义的状态 2020-7-28 11:00:43
          if (ALL_STATUS.indexOf(retJson.status) > -1) {
            dispatch(updateDownloadSoftStatus(retJson));
          }

          logNative(`通知 APPSTORE_DOWNLOADDING_STATUS PC软件安装 状态`, retJson);
          if (
            retJson.status === INSTALL_FAILED ||
            retJson.status === INSTALL_FINISHED ||
            retJson.status === DOWNLOAD_CANCELED
          ) {
            dispatch(removeDownload(retJson.SoftID));
          }
          if (retJson.status === INSTALL_FINISHED) {
            dispatch(getLocalSoft());
          }
        } catch (error) {
          logNative(`通知 APPSTORE_DOWNLOADDING_STATUS JSON 解析错误:${error}`, error);
        }
      }

      // 获取更新列表
      if (message.getMsgcategory() === 'APPSTORE_GETUPGRADE_SOFTLIST') {
        const ret = PCStoreJsBridge.arrayToUTF8String(message.getMessageproto());
        try {
          const retJson = JSON.parse(ret);
          logNative(
            `通知 APPSTORE_GETUPGRADE_SOFTLIST 获取可更新软件耗时: ${new Date().getTime() -
              Number(window.getUpdateStartTime)}`,
            retJson,
            true,
            true,
          );
          let uniqRet = [];
          if (retJson.length && retJson.length > 0) {
            // 去重
            // uniqRet = uniqBy(retJson, e => e.SoftID);
            uniqRet = retJson;
          }
          dispatch(getUpgradeSofts(uniqRet));
        } catch (error) {
          logNative(`通知 APPSTORE_GETUPGRADE_SOFTLIST JSON 解析错误:${error}`, error);
        }
      }

      // 壁纸下载进度变化通知
      if (message.getMsgcategory() === 'APPSTORE_DOWNWALLPAPER_PROGRESS') {
        const ret = PCStoreJsBridge.arrayToUTF8String(message.getMessageproto());
        try {
          let retJson = JSON.parse(ret);
          retJson = { ...retJson, id: retJson.id };
          logNative(`通知 APPSTORE_DOWNWALLPAPER_PROGRESS  壁纸下载进度`, retJson, true, true);
          dispatch(updateDownloadImgProgress({ wallpaper: retJson }));
        } catch (error) {
          logNative(`通知 APPSTORE_DOWNWALLPAPER_PROGRESS JSON 解析错误:${error}`, error);
        }
      }

      // 壁纸下载状态更新
      if (message.getMsgcategory() === 'APPSTORE_DOWNWALLPAPER_STATUS') {
        const ret = PCStoreJsBridge.arrayToUTF8String(message.getMessageproto());
        try {
          let retJson = JSON.parse(ret);
          retJson = { ...retJson, id: retJson.id };
          logNative(
            `通知 APPSTORE_DOWNWALLPAPER_STATUS  壁纸下载状态更新${JSON.stringify(retJson)}`,
            retJson,
            true,
            true,
          );
          if (retJson.status === WP_DOWNLOAD_FAILED || retJson.status === WP_DOWNLOAD_CANCELED) {
            dispatch(removeDownloadImg({ wallpaper: retJson }));
          } else {
            dispatch(updateDownloadImgStatus({ wallpaper: retJson }));
          }

          if (retJson.status === WP_DOWNLOAD_FINISHED) {
            setTimeout(() => {
              dispatch(removeDownloadImg({ wallpaper: retJson }));
            }, 700);
          }
        } catch (error) {
          logNative(`通知 APPSTORE_DOWNWALLPAPER_STATUS JSON 解析错误:${error}`, error);
        }
      }

      // 软件卸载残留清理结果通知
      if (message.getMsgcategory() === 'APPSTORE_REQ_DELETE_RESIDUAL_RESULT') {
        const ret = PCStoreJsBridge.arrayToUTF8String(message.getMessageproto());
        const retJson = JSON.parse(ret);
        try {
          logNative(
            `通知 APPSTORE_REQ_DELETE_RESIDUAL_RESULT 删除残留状态${ret}`,
            retJson,
            true,
            true,
          );
          // 卸载残留清理
          const { succ, softGUID } = retJson;
          if (succ) {
            dispatch(
              modifyResidualData({
                softGUID,
                status: clearStatus.CLEAR_SUCC,
              }),
            );
          } else {
            dispatch(
              modifyResidualData({
                softGUID,
                status: clearStatus.CLEAR_ERR,
              }),
            );
          }
        } catch (errors) {
          logNative(`通知 APPSTORE_REQ_DELETE_RESIDUAL_RESULT JSON 解析错误${errors}`, errors);
        }
      }
      // 软件卸载-扫描残留文件进度通知
      if (message.getMsgcategory() === 'APPSTORE_REQ_SOFTWARE_RESIDUAL_PERCENT') {
        const ret = PCStoreJsBridge.arrayToUTF8String(message.getMessageproto());
        const retJson = JSON.parse(ret);
        const { softGUID, percent } = retJson;
        try {
          logNative(
            `通知 APPSTORE_REQ_SOFTWARE_RESIDUAL_PERCENT 卸载扫描残留进度${ret}`,
            retJson,
            true,
            true,
          );
          dispatch(
            modifyResidualData({
              softGUID,
              percent,
            }),
          );
        } catch (errors) {
          logNative(`通知 APPSTORE_REQ_SOFTWARE_RESIDUAL_PERCENT JSON 解析错误${errors}`, errors);
        }
      }
      // 卸载扫描 -扫描残留文件结果通知
      if (message.getMsgcategory() === 'APPSTORE_REQ_SOFTWARE_RESIDUAL_RESULT') {
        const ret = PCStoreJsBridge.arrayToUTF8String(message.getMessageproto());
        const retJson = JSON.parse(ret);
        const { softGUID, code } = retJson;
        try {
          logNative(
            `通知 APPSTORE_REQ_SOFTWARE_RESIDUAL_RESULT 卸载状态${ret}`,
            retJson,
            true,
            true,
          );
          dispatch(
            modifyResidualData({
              softGUID,
              percent: 100,
            }),
          );
          // 扫描结果--没有残留
          if (code === 2) {
            dispatch(
              modifyResidualData({
                softGUID,
                hasResidual: false,
              }),
            );
          }
          // 扫描结果-扫描失败
          if (code === 3) {
            dispatch(modifyResidualData({ softGUID, status: clearStatus.CLEAR_ERR }));
            dispatch(removeTempResidual({ softGUID }));
            dispatch(removeResidual({ softGUID }));
            return;
          }
          if (code !== 2 && code !== 3) {
            dispatch(
              modifyResidualData({
                softGUID,
                totalNum: retJson.residualResult.residualInfo.totalNum,
                totalSize: retJson.residualResult.residualInfo.totalSize,
                residualResult: retJson.residualResult.residualInfo,
              }),
            );
          }
        } catch (errors) {
          logNative(`通知 APPSTORE_REQ_SOFTWARE_RESIDUAL_RESULT JSON 解析错误${errors}`, errors);
        }
      }
      // 软件卸载-开始扫描状态消息
      if (message.getMsgcategory() === 'APPSTORE_START_SCAN_RESIDUAL') {
        const ret = PCStoreJsBridge.arrayToUTF8String(message.getMessageproto());
        try {
          const retJson = JSON.parse(ret);
          const { softGUID } = retJson;
          logNative(`通知 APPSTORE_START_SCAN_RESIDUAL 扫描状态${ret}`, softGUID, true, true);
          // 8s后改变状态为监听状态
          const timer = setTimeout(() => {
            // 卸载残留
            dispatch(
              addTempResidual({
                softGUID,
                ...retJson,
                hasResidual: true,
                percent: 0,
                status: 'SCANING',
              }),
            );
            clearTimeout(timer);
          }, 8000);
          // 扫描状态超过30s 停止扫描
          const timers = setTimeout(() => {
            dispatch(
              stopScanSoftwareResidual({
                softGUID,
                ...retJson,
              }),
            );
            clearTimeout(timers);
          }, 30000);
        } catch (error) {
          logNative(`通知 APPSTORE_START_SCAN_RESIDUAL JSON 解析错误${error}`, error);
        }
      }
      // 软件卸载-卸载结果
      if (message.getMsgcategory() === 'APPSTORE_UNINSTALL_RESULT') {
        const ret = PCStoreJsBridge.arrayToUTF8String(message.getMessageproto());
        try {
          const retJson = JSON.parse(ret);
          logNative(`通知 APPSTORE_UNINSTALL_RESULT 卸载状态${ret}`, retJson, true, true);
          // 卸载残留
          if (retJson.hasResidual && retJson.UninstallStatus === 2) {
            // 剔除因为返回扫描状态临时假如的卸载残留软件

            const residualInfo = {
              residualResult: retJson.residualInfo,
              status: 'init',
              totalNum: retJson.residualInfo.totalNum,
              totalSize: retJson.residualInfo.totalSize,
              hasResidual: true,
              percent: 100,
            };
            if (retJson.residualInfo.totalSize > 200 * 1024 * 1024) {
              // 移除不存在残留的扫描状态
              dispatch(removeTempResidual({ softGUID: retJson.softGUID }));
            } else {
              dispatch(
                addTempResidual({
                  softGUID: retJson.softGUID,
                  ...residualInfo,
                }),
              );
            }

            // 不再获取安装列表，成功直接从当前列表移除
            dispatch(removeUninstall(retJson));
            dispatch(cancelUninstall(retJson));
          }
          // 卸载成功
          if (!retJson.hasResidual && retJson.UninstallStatus === 2) {
            // 不再获取安装列表，成功直接从当前列表移除
            dispatch(removeUninstall(retJson));
            // 移除不存在残留的扫描状态
            dispatch(removeTempResidual({ softGUID: retJson.softGUID }));
            dispatch(cancelUninstall(retJson));
          }
          // 卸载失败或取消卸载
          if (retJson.UninstallStatus === 3) {
            dispatch(cancelUninstall(retJson));
          }
          // 其他卸载结果 --- 回复状态-至卸载
          if (retJson.UninstallStatus !== 2 && retJson.UninstallStatus !== 3) {
            dispatch(removeUninstall(retJson));
          }
        } catch (error) {
          logNative(`通知 APPSTORE_UNINSTALL_RESULT JSON 解析错误${error}`, error);
        }
      }

      // 用户反馈-反馈提交结果
      if (message.getMsgcategory() === 'APPSTORE_REQ_SUBMIT_FEEDBACK_RESULT') {
        const ret = PCStoreJsBridge.arrayToUTF8String(message.getMessageproto());
        try {
          const retJson = JSON.parse(ret);
          logNative(
            `通知 APPSTORE_REQ_SUBMIT_FEEDBACK_RESULT 反馈提交状态${ret}`,
            retJson,
            true,
            true,
          );
          // 保存反馈状态
          dispatch(setFeedbackTask(retJson));
        } catch (error) {
          logNative(`通知 setFeedbackTask JSON 解析错误${error}`, error);
        }
      }
    });
  }, []);

  // 进入页面就获取相关信息 路径 下载路径 静默安装路径 用户userToken
  useEffect(() => {
    const notNewUser = localStorage.getItem('WELCOME_USER');
    const userTypeIds = localStorage.getItem('WELCOME_USER_TYPE');
    dispatch(
      getWelcomeData({
        ids: notNewUser !== 'NO' ? [] : (userTypeIds && JSON.parse(userTypeIds)) || ['1'],
      }),
    );
    PCStoreJsBridge.getOtherInfo().then(res => {
      dispatch(setOtherInfo(res));
    });
    PCStoreJsBridge.getQuietInstallPath().then(res => {
      dispatch(setQuietInstallPath({ ...res }));
    });

    // 更新用户userToken
    const userTokenLS = { data: localStorage.getItem(cacheKeys.X_LESYNC_TOKEN) };
    PCStoreJsBridge.getUserTokenInfo(userTokenLS).then(res => {
      if (!res) {
        localStorage.setItem(cacheKeys.X_LESYNC_TOKEN, '');
      }
    });
  }, [dispatch]);

  useEffect(() => {
    if (welcomeIDs && welcomeIDs.length > 0) {
      dispatch(addWelcomeIdToDownload(welcomeIDs));
      dispatch(setDownloadVisible(true));
    }
  }, [welcomeIDs]);

  // 进入页面就获取支持静默安装的应用
  useEffect(() => {
    dispatch(getSilentInstallSoft());

    // 晚一秒去获取 卸载磁铁和右键菜单开关状态
    setTimeout(() => {
      getInitInfoSaveToStartInfo();
    }, 1000);
  }, []);

  useEffect(() => {
    if (window.PcstoreCanChangeSize) {
      window.pcmAllowStretchWindow && window.pcmAllowStretchWindow();
    }
  }, []);

  /**
   * 讲一些初始化信息放入startInfo 中
   */
  const getInitInfoSaveToStartInfo = useCallback(() => {
    // 进入页面获取 卸载磁铁和右键菜单开关状态  并且写入到startInfo 中去
    PCStoreJsBridge.getPCStoreTileStatus().then(res => {
      const startInfo = JSON.parse(localStorage.getItem(cacheKeys.START_INFO) || '{}');
      localStorage.setItem(
        cacheKeys.START_INFO,
        JSON.stringify({
          ...startInfo,
          storeTileInfo: res.succ ? { enable: res.enable, cloudEnable: res.cloudEnable } : null,
        }),
      );
    });
    // 这里设置右键菜单开关状态
    PCStoreJsBridge.getRightMenuInfo().then(res => {
      const startInfo = JSON.parse(localStorage.getItem(cacheKeys.START_INFO) || '{}');
      localStorage.setItem(
        cacheKeys.START_INFO,
        JSON.stringify({
          ...startInfo,
          rightMenuInfo: res.succ ? { enable: res.enable, cloudEnable: res.cloudEnable } : null,
        }),
      );
    });
  });

  // 默认地址
  let defaultPage = getDefaultPage();

  return (
    <Router>
      <Layout>
        <Switch>
          <Route exact path={`${CONTEXT}/`} render={() => <Redirect to={`${defaultPage}`} />} />
          <Route
            exact
            path={`${CONTEXT}/recommend`}
            render={props => <Recommend></Recommend>}
          ></Route>
          <Route path={`${CONTEXT}/education`}>
            <KeepAlive name="RecoEducation">
              <RecoEducation></RecoEducation>
            </KeepAlive>
          </Route>

          <Route path={`${CONTEXT}/edudetail`}>
            <EduDetail></EduDetail>
          </Route>
          <Route exact path={`${CONTEXT}/work`} render={props => <RecoWork></RecoWork>}></Route>
          <Route path={`${CONTEXT}/game`} component={Game} />
          <Route exact path={`${CONTEXT}/category`} render={props => <Category></Category>}></Route>
          <Route exact path={`${CONTEXT}/topn`} component={Topn} />
          <Route exact path={`${CONTEXT}/topnmore/:code/:title`} component={TopnMore} />
          <Route exact path={`${CONTEXT}/shop`} component={Shop} />
          <Route exact path={`${CONTEXT}/update`} render={props => <Update></Update>}></Route>
          <Route
            exact
            path={`${CONTEXT}/uninstall`}
            render={props => <Uninstall></Uninstall>}
          ></Route>
          <Route exact path={`${CONTEXT}/detail/soft/:id/:bizInfo`} component={Detail} />
          <Route
            exact
            path={`${CONTEXT}/search/soft`}
            render={props => <SearchResult></SearchResult>}
          ></Route>
          <Route exact path={`${CONTEXT}/guesslikesmore`} component={GuessLikesMore} />
          <Route exact path={`${CONTEXT}/rankmore`} component={RankMore} />
          <Route exact path={`${CONTEXT}/dt`} component={DataTest} />
          <Route exact path={`${CONTEXT}/arecommend`} render={props => <ARecommend></ARecommend>} />
          <Route exact path={`${CONTEXT}/acategory`} component={ACategory} />
          <Route exact path={`${CONTEXT}/adetail/:pn/:vc/:bizInfo`} component={ADetail} />
          <Route exact path={`${CONTEXT}/special`} render={props => <Special></Special>}></Route>
          <Route
            exact
            path={`${CONTEXT}/speciallist`}
            render={props => <SpecialList></SpecialList>}
          ></Route>
          <Route
            exact
            path={`${CONTEXT}/wprecommend`}
            render={props => <WallpaperRecommend></WallpaperRecommend>}
          ></Route>
          <Route
            exact
            path={`${CONTEXT}/wpcategory`}
            render={props => <WallpaperCategory></WallpaperCategory>}
          ></Route>
          <Route exact path={`${CONTEXT}/wpmine/:tagId`} component={WallpaperMine} />
          <Route exact path={`${CONTEXT}/wpdetail`} component={WallpaperDetail} />
          <Route
            exact
            path={`${CONTEXT}/wpauthorlist`}
            render={props => <WallPaperAuthorList></WallPaperAuthorList>}
          ></Route>
          <Route
            exact
            path={`${CONTEXT}/wpauthordetail`}
            render={props => <WallPaperAuthorDetail></WallPaperAuthorDetail>}
          ></Route>
          <Route
            exact
            path={`${CONTEXT}/wprank`}
            render={props => <WallPaperRank></WallPaperRank>}
          ></Route>

          <Route
            exact
            path={`${CONTEXT}/otherpage`}
            render={props => <OtherPage></OtherPage>}
          ></Route>
          <Route
            exact
            path={`${CONTEXT}/othertest`}
            render={props => <OtherTest></OtherTest>}
          ></Route>
          <Route path={`${CONTEXT}/collection`}>
            <Collection></Collection>
          </Route>
          <Route path={`${CONTEXT}/erroredu`}>
            <ErrorPageEdu></ErrorPageEdu>
          </Route>

          <Route
            exact
            path={`${CONTEXT}/forumlist`}
            render={props => <ForumList></ForumList>}
          ></Route>
          <Route
            exact
            path={`${CONTEXT}/forumlistpublish`}
            render={props => <ForumListPublish></ForumListPublish>}
          ></Route>
          <Route
            exact
            path={`${CONTEXT}/forumlistcollect`}
            render={props => <ForumListCollect></ForumListCollect>}
          ></Route>
          <Route
            exact
            path={`${CONTEXT}/forumdetail`}
            render={props => <ForumDetail></ForumDetail>}
          ></Route>
          <Route
            exact
            path={`${CONTEXT}/formpublish`}
            render={props => <ForumPublish></ForumPublish>}
          ></Route>

          <Route path={`${CONTEXT}/error`}>
            <ErrorPage></ErrorPage>
          </Route>
        </Switch>
      </Layout>
    </Router>
  );
}

export default App;
